import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { useUpdateOrganizationNameMutation } from "../../../../../../service/user-service"
import styles from "../edit-account-name-modal/edit-account-name-modal.module.scss"
import ownStyles from "./edit-corporate-name-modal.module.scss"

interface EditCorporateNameModalProps {
  isOpen: boolean
  onClose: () => void
  organizationName: string
  /**
   * Verified/registered corporate names may be locked from self-service edits
   * (legal/compliance) — renders the input disabled with a support notice.
   */
  isEditable?: boolean
}

interface FormValues {
  organizationName: string
}

export const EditCorporateNameModal = ({
  isOpen,
  onClose,
  organizationName,
  isEditable = true
}: EditCorporateNameModalProps) => {
  const { t } = useTranslation()
  const [updateOrganizationName, { isLoading }] = useUpdateOrganizationNameMutation()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: { organizationName }
  })

  useEffect(() => {
    if (isOpen) {
      reset({ organizationName })
    }
  }, [isOpen, organizationName, reset])

  const onSubmit = async (data: FormValues) => {
    try {
      await updateOrganizationName({
        organizationName: data.organizationName.trim()
      }).unwrap()

      toast.success(t("account.web.corporateNameUpdated"))
      onClose()
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.failedToUpdateCorporateName"))
    }
  }

  const handleCancel = () => {
    reset({ organizationName })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isLoading}
            aria-label={t("account.web.close")}
          >
            ×
          </button>
          <h2 className={styles.modalTitle}>{t("account.corporateName")}</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.body}>
            <div className={styles.fieldsCard}>
              <div className={styles.inputField}>
                <label className={styles.inputLabel}>{t("account.corporateName")}</label>
                <input
                  type="text"
                  className={`${styles.input} ${errors.organizationName ? styles.inputError : ""}`}
                  placeholder={t("account.web.enterCorporateName")}
                  disabled={!isEditable || isLoading}
                  {...register("organizationName", {
                    required: t("account.web.pleaseEnterAName"),
                    minLength: {
                      value: 2,
                      message: t("account.web.corporateNameMinLength")
                    }
                  })}
                />
                {errors.organizationName && <p className={styles.errorText}>{errors.organizationName.message}</p>}
              </div>
              <p className={styles.helperText}>
                {t("account.enterCompanysFullLegalName")}
              </p>
              {!isEditable && (
                <p className={ownStyles.lockedNotice}>
                  {t("account.web.corporateNameLockedNotice")}
                </p>
              )}
            </div>

            <button type="submit" className={styles.saveButton} disabled={isLoading || !isEditable}>
              {isLoading ? t("account.web.saving") : t("account.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
